import { create } from "zustand";

type ChatFlagKey = string;

interface ChatFlagsState {
	archived: Record<ChatFlagKey, true>;
	muted: Record<ChatFlagKey, true>;
	reported: Record<ChatFlagKey, true>;
	notifMode: Record<ChatFlagKey, "all" | "mentions" | "none">;
	registrationServer: string;

	isArchived: (accountId: string, chatId: string) => boolean;
	isMuted: (accountId: string, chatId: string) => boolean;
	isReported: (accountId: string, chatId: string) => boolean;
	getNotifMode: (accountId: string, chatId: string) => "all" | "mentions" | "none";

	setArchived: (accountId: string, chatId: string, value: boolean) => void;
	setMuted: (accountId: string, chatId: string, value: boolean) => void;
	setReported: (accountId: string, chatId: string, value: boolean) => void;
	setNotifMode: (accountId: string, chatId: string, mode: "all" | "mentions" | "none") => void;
	setRegistrationServer: (server: string) => void;
}

const STORAGE_KEY = "cheogram-next:chat-flags:v1";

type Persisted = Pick<ChatFlagsState, "archived" | "muted" | "reported" | "notifMode" | "registrationServer">;

// Read VITE_REGISTRATION_SERVER through a guard so this module can also load
// under plain node (tsx for tests) where import.meta.env is undefined.
const defaultRegistrationServer = (() => {
	const env = (import.meta as { env?: Record<string, string | undefined> }).env;
	return env?.["VITE_REGISTRATION_SERVER"] ?? "snikket.holm.chat";
})();

const loadPersisted = (): Persisted => {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) throw new Error("no data");
		const parsed = JSON.parse(raw) as Partial<Persisted>;
		return {
			archived: parsed.archived ?? {},
			muted: parsed.muted ?? {},
			reported: parsed.reported ?? {},
			notifMode: parsed.notifMode ?? {},
			registrationServer: parsed.registrationServer ?? defaultRegistrationServer,
		};
	} catch {
		return {
			archived: {},
			muted: {},
			reported: {},
			notifMode: {},
			registrationServer: defaultRegistrationServer,
		};
	}
};

const persistSnapshot = (state: Persisted) => {
	try {
		localStorage.setItem(
			STORAGE_KEY,
			JSON.stringify({
				archived: state.archived,
				muted: state.muted,
				reported: state.reported,
				notifMode: state.notifMode,
				registrationServer: state.registrationServer,
			}),
		);
	} catch {
		// quota or disabled storage — silently degrade
	}
};

const k = (accountId: string, chatId: string) => `${accountId}\x00${chatId}`;

const initial = loadPersisted();

export const useChatFlagsStore = create<ChatFlagsState>((set, get) => ({
	...initial,

	isArchived: (accountId, chatId) => !!get().archived[k(accountId, chatId)],
	isMuted: (accountId, chatId) => !!get().muted[k(accountId, chatId)],
	isReported: (accountId, chatId) => !!get().reported[k(accountId, chatId)],
	getNotifMode: (accountId, chatId) => get().notifMode[k(accountId, chatId)] ?? "all",

	setArchived: (accountId, chatId, value) =>
		set((s) => {
			const next = { ...s.archived };
			if (value) next[k(accountId, chatId)] = true;
			else delete next[k(accountId, chatId)];
			persistSnapshot({ ...s, archived: next });
			return { archived: next };
		}),

	setMuted: (accountId, chatId, value) =>
		set((s) => {
			const next = { ...s.muted };
			if (value) next[k(accountId, chatId)] = true;
			else delete next[k(accountId, chatId)];
			persistSnapshot({ ...s, muted: next });
			return { muted: next };
		}),

	setReported: (accountId, chatId, value) =>
		set((s) => {
			const next = { ...s.reported };
			if (value) next[k(accountId, chatId)] = true;
			else delete next[k(accountId, chatId)];
			persistSnapshot({ ...s, reported: next });
			return { reported: next };
		}),

	setNotifMode: (accountId, chatId, mode) =>
		set((s) => {
			const next = { ...s.notifMode, [k(accountId, chatId)]: mode };
			persistSnapshot({ ...s, notifMode: next });
			return { notifMode: next };
		}),

	setRegistrationServer: (server) =>
		set((s) => {
			persistSnapshot({ ...s, registrationServer: server });
			return { registrationServer: server };
		}),
}));
